// Version: 8.1.3 - Fix candidate cards deletion from kanban
import React from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';

import { useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { PublicLanguageProvider } from './contexts/PublicLanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import SuperAdminRoute from './components/SuperAdminRoute';
import AdminRoute from './components/AdminRoute';
import PublicHeader from './components/PublicHeader';
import PrivateLayout from './components/PrivateLayout';

// Public Pages
import HomePage from './pages/public/HomePage';
import AboutPage from './pages/public/AboutPage';
import FeaturesPage from './pages/public/FeaturesPage';
import ContactPage from './pages/public/ContactPage';
import SecurityPrivacyPage from './pages/public/SecurityPrivacyPage';
import LoginPage from './pages/auth/LoginPage';
import SignupPage from './pages/auth/SignupPage';
import VerifyEmailPage from './pages/auth/VerifyEmailPage';
import ForgotPasswordPage from './pages/auth/ForgotPasswordPage';
import ResetPasswordPage from './pages/auth/ResetPasswordPage';
import JobApplicationPage from './pages/public/JobApplicationPage';
import BookingPage from './pages/public/BookingPage';

// Private Pages
import DashboardPage from './pages/dashboard/DashboardPage';
import OffersPage from './pages/dashboard/OffersPage';
import CVsPage from './pages/dashboard/CVsPage';
import AnalyzerPage from './pages/dashboard/AnalyzerPage';
import ResultsPage from './pages/dashboard/ResultsPage';
import SettingsPage from './pages/dashboard/SettingsPage';
import HRManagementPage from './pages/dashboard/HRManagementPage';
import KanbanPage from './pages/dashboard/KanbanPage';
import NotesPage from './pages/dashboard/NotesPage';
import ApplicationsPage from './pages/dashboard/ApplicationsPage';
import AppointmentsPage from './pages/dashboard/AppointmentsPage';
import SuperAdminEnvPage from './pages/dashboard/SuperAdminEnvPage';

// Super Admin Pages
import SuperAdminDashboard from './pages/dashboard/SuperAdminDashboard';
import SuperAdminUsers from './pages/dashboard/SuperAdminUsers';
import SuperAdminCommunityPage from './pages/dashboard/SuperAdminCommunityPage';

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <LanguageProvider>
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-center"
          >
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
            <p className="text-gray-600">Chargement...</p>
          </motion.div>
        </div>
      </LanguageProvider>
    );
  }

  return (
    <LanguageProvider>
    <div className="min-h-screen bg-gray-50">
      <Routes>
        {/* Public Job Application Routes (standalone, no header) */}
        <Route path="/apply/:publicUrl" element={<JobApplicationPage />} />
        <Route path="/jobs/:publicUrl" element={<JobApplicationPage />} />
        
        {/* Public Booking Routes (standalone, no header) */}
        <Route path="/rdv/:bookingSlug" element={<BookingPage />} />
        
        {/* Auth Routes (without PublicHeader) */}
        <Route
          path="/login"
          element={
            !user ? (
              <PublicLanguageProvider>
                <LoginPage />
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/signup"
          element={
            !user ? (
              <PublicLanguageProvider>
                <SignupPage />
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/verify-email"
          element={
            !user ? (
              <PublicLanguageProvider>
                <VerifyEmailPage />
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/forgot-password"
          element={
            !user ? (
              <PublicLanguageProvider>
                <ForgotPasswordPage />
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        <Route
          path="/reset-password"
          element={
            !user ? (
              <PublicLanguageProvider>
                <ResetPasswordPage />
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />
        
        {/* Public Routes (with PublicHeader) */}
        <Route
          path="/*"
          element={
            !user ? (
              <PublicLanguageProvider>
                <PublicHeader />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/qui-sommes-nous" element={<AboutPage />} />
                  <Route path="/fonctionnalites" element={<FeaturesPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/securite-confidentialite" element={<SecurityPrivacyPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </PublicLanguageProvider>
            ) : (
              <Navigate to="/dashboard" replace />
            )
          }
        />

        {/* Private Routes */}
        <Route
          path="/dashboard/*"
          element={
            <ProtectedRoute>
              <PrivateLayout>
                <Routes>
                  <Route index element={user?.role === 'super_admin' ? <SuperAdminDashboard /> : <DashboardPage />} />
                  <Route path="offres" element={<OffersPage />} />
                  <Route path="cvs" element={<CVsPage />} />
                  <Route path="kanban" element={<KanbanPage />} />
                  <Route path="notes" element={<NotesPage />} />
                  <Route path="candidatures" element={<ApplicationsPage />} />
                  <Route path="applications" element={<ApplicationsPage />} />
                  <Route path="analyseur" element={<AnalyzerPage />} />
                  <Route path="resultats/*" element={<ResultsPage />} />
                  <Route path="rendez-vous/*" element={<AppointmentsPage />} />
                  <Route path="parametres" element={<SettingsPage />} />
                  <Route path="gestion-rh" element={
                    <AdminRoute>
                      <HRManagementPage />
                    </AdminRoute>
                  } />
                  
                  {/* Super Admin Routes */}
                  <Route path="super-admin" element={
                    <SuperAdminRoute>
                      <SuperAdminDashboard />
                    </SuperAdminRoute>
                  } />
                  <Route path="super-admin/users" element={
                    <SuperAdminRoute>
                      <SuperAdminUsers />
                    </SuperAdminRoute>
                  } />
                  <Route path="super-admin/env" element={
                    <SuperAdminRoute>
                      <SuperAdminEnvPage />
                    </SuperAdminRoute>
                  } />
                  <Route path="super-admin/community" element={
                    <SuperAdminRoute>
                      <SuperAdminCommunityPage />
                    </SuperAdminRoute>
                  } />
                  
                  <Route path="*" element={<Navigate to="/dashboard" replace />} />
                </Routes>
              </PrivateLayout>
            </ProtectedRoute>
          }
        />

        {/* Pricing routes are deprecated (free access) */}
        <Route path="/tarification" element={<Navigate to={user ? "/dashboard" : "/contact"} replace />} />
        <Route path="/pricing" element={<Navigate to={user ? "/dashboard" : "/contact"} replace />} />

        {/* Catch all route */}
        <Route path="*" element={<Navigate to={user ? "/dashboard" : "/"} replace />} />
      </Routes>
    </div>
    </LanguageProvider>
  );
}

export default App;
